import { useState, useEffect, useRef } from "react";
import { MapPin, Plus, Trash2, Globe, Search, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export interface LocationRadius {
  id?: string;
  label: string;
  lat: number;
  lng: number;
  radius_km: number;
  is_whole_country: boolean;
}

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
}

interface LocationRadiusPickerProps {
  value: LocationRadius[];
  onChange: (locations: LocationRadius[]) => void;
  disabled?: boolean;
}

export function LocationRadiusPicker({ value, onChange, disabled }: LocationRadiusPickerProps) {
  const { t } = useI18n();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<NominatimResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [addingRadius, setAddingRadius] = useState(50);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasWholeCZ = value.some((l) => l.is_whole_country);

  // Click outside to close dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const searchPlaces = (query: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const resp = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&countrycodes=cz,sk&limit=6&q=${encodeURIComponent(query)}`,
          { headers: { "User-Agent": "cenik.ai/1.0" } }
        );
        const data: NominatimResult[] = await resp.json();
        setSearchResults(data);
        setShowDropdown(data.length > 0);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
  };

  const addLocation = (result: NominatimResult) => {
    // Shorten label to city name
    const parts = result.display_name.split(",");
    const label = parts[0].trim() + (parts.length > 1 ? `, ${parts[1].trim()}` : "");

    const newLoc: LocationRadius = {
      label,
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      radius_km: addingRadius,
      is_whole_country: false,
    };
    onChange([...value, newLoc]);
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
    setAddingRadius(50);
  };

  const addWholeCZ = () => {
    const newLoc: LocationRadius = {
      label: t("filter.wholeCZ"),
      lat: 49.8,
      lng: 15.5,
      radius_km: 0,
      is_whole_country: true,
    };
    onChange([...value, newLoc]);
  };

  const removeLocation = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateRadius = (index: number, radius: number) => {
    const updated = [...value];
    updated[index] = { ...updated[index], radius_km: radius };
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {/* Existing locations */}
      {value.map((loc, i) => (
        <div
          key={i}
          className="flex items-center gap-2 bg-accent/50 rounded-lg px-3 py-2 border border-border"
        >
          {loc.is_whole_country ? (
            <Globe className="w-4 h-4 text-primary flex-shrink-0" />
          ) : (
            <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-foreground truncate block">{loc.label}</span>
            {!loc.is_whole_country && (
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="range"
                  min={5}
                  max={200}
                  step={5}
                  value={loc.radius_km}
                  onChange={(e) => updateRadius(i, parseInt(e.target.value))}
                  disabled={disabled}
                  className="flex-1 h-1.5 accent-primary"
                />
                <span className="text-xs text-muted-foreground w-14 text-right">{loc.radius_km} km</span>
              </div>
            )}
          </div>
          {!disabled && (
            <button
              onClick={() => removeLocation(i)}
              className="p-1 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}

      {/* Add new location */}
      {!disabled && (
        <div className="space-y-2">
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchPlaces(e.target.value);
                }}
                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                className="w-full pl-9 pr-8 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder={t("filter.searchCity")}
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(""); setSearchResults([]); setShowDropdown(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {showDropdown && (
              <div className="absolute z-20 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {searching && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">{t("filter.searching")}...</div>
                )}
                {searchResults.map((result, i) => (
                  <button
                    key={i}
                    onClick={() => addLocation(result)}
                    className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors flex items-center gap-2"
                  >
                    <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{result.display_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Radius preset for next addition */}
          {searchQuery.length >= 2 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{t("filter.radius")}:</span>
              <input
                type="range"
                min={5}
                max={200}
                step={5}
                value={addingRadius}
                onChange={(e) => setAddingRadius(parseInt(e.target.value))}
                className="flex-1 h-1.5 accent-primary"
              />
              <span className="text-xs text-muted-foreground w-14 text-right">{addingRadius} km</span>
            </div>
          )}

          {/* Whole CZ button */}
          {!hasWholeCZ && (
            <button
              onClick={addWholeCZ}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-dashed border-border text-muted-foreground rounded-lg hover:bg-accent hover:text-foreground transition-colors w-full justify-center"
            >
              <Globe className="w-3.5 h-3.5" />
              {t("filter.addWholeCZ")}
            </button>
          )}
        </div>
      )}

      {value.length === 0 && disabled && (
        <p className="text-xs text-muted-foreground italic">{t("filter.noLocationsSet")}</p>
      )}
    </div>
  );
}
